import { z } from 'zod';

import { httpStatusCodeSchema } from '../data-contract/error';
import { userRoleSchema } from '../data-contract/user-role';

import type { TOptions } from 'i18next';
import type { RequiredDeep } from 'type-fest';

const roleSchema = z
    .object({
        description: z.string(),
        label: z.string().default('not set'),
        title: z.string(),
    })
    .partial();

const errorTranslationSchema = z
    .object({
        action: z.string(),
        message: z.string(),
        title: z.string(),
    })
    .partial();

const onboardingProfileVisibilitySettingSchema = z
    .object({
        subtitle: z.string(),
        title: z.string(),
    })
    .partial();

export const translationResourceSchema = z
    .object({
        common: z
            .object({
                add: z.string(),
                adjust: z.string(),
                assign: z.string(),
                back: z.string(),
                'back-to-jsa': z.string(),
                bookmark: z.string(),
                boost: z.string(),
                brand: z.string(),
                by: z.string(),
                cancel: z.string(),
                career: z.string(),
                close: z.string(),
                collapse: z.string(),
                completed: z.string(),
                confirm: z.string(),
                continue: z.string(),
                copy: z.string(),
                create: z.string(),
                'create-account': z.string(),
                cut: z.string(),
                date: z.string(),
                days: z.string(),
                delete: z.string(),
                'delete-my-account': z.string(),
                'deleted-account': z.string(),
                description: z.string(),
                discard: z.string(),
                done: z.string(),
                'download-manual': z.string(),
                'download-pdf': z.string(),
                edit: z.string(),
                email: z.string(),
                'email-hidden': z.string(),
                end: z.string(),
                enums: z
                    .object({
                        duration: z
                            .object({
                                'fixed-term': z.string(),
                                permanent: z.string(),
                            })
                            .partial(),
                        'job-profile-review-state': z
                            .object({
                                approved: z.string(),
                                assigned: z.string(),
                            })
                            .partial(),
                        'job-seeking-status': z
                            .object({
                                'not open': z.string(),
                                open: z.string(),
                            })
                            .partial(),
                        'job-state': z
                            .object({
                                archived: z.string(),
                                draft: z.string(),
                                hired: z.string(),
                                published: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'relocation-preference': z
                            .object({
                                'not open': z.string(),
                                open: z.string(),
                                'relocation only': z.string(),
                            })
                            .partial(),
                        'seniority-level': z
                            .object({
                                0: z.string(),
                                1: z.string(),
                                10: z.string(),
                                2: z.string(),
                                3: z.string(),
                                4: z.string(),
                                5: z.string(),
                                6: z.string(),
                                7: z.string(),
                                8: z.string(),
                                9: z.string(),
                            })
                            .partial(),
                        skillType: z
                            .object({
                                hard: z.string(),
                                language: z.string(),
                                soft: z.string(),
                            })
                            .partial(),
                        'work-place': z
                            .object({
                                'on-site': z.string(),
                                remote: z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                exclude: z.string(),
                expand: z.string(),
                export: z.string(),
                exporting: z.string(),
                'featured-at': z.string(),
                feedback: z.string(),
                filters: z
                    .object({
                        apply: z.string(),
                        city: z.string(),
                        'experience-level': z.string(),
                        'job-seeking': z
                            .object({
                                'not open': z.string(),
                                open: z.string(),
                            })
                            .partial(),
                        'job-seeking-status': z.string(),
                        relocation: z.string(),
                        'relocation-preference': z
                            .object({
                                'not open': z.string(),
                                open: z.string(),
                                'relocation only': z.string(),
                            })
                            .partial(),
                        state: z.string(),
                    })
                    .partial(),
                finish: z.string(),
                help: z.string(),
                home: z.string(),
                imprint: z.string(),
                'in-progress': z.string(),
                language: z.string(),
                languages: z.object({
                    'de-DE': z.string(),
                    'en-US': z.string(),
                    'es-ES': z.string(),
                }),
                'load-more': z.string(),
                login: z.string(),
                'login-button': z.string(),
                login2: z.string(),
                login3: z.string(),
                logout: z.string(),
                more: z.string(),
                new: z.string(),
                no: z.string(),
                'no-selection': z.string(),
                onboarding: z.string(),
                options: z.string(),
                others: z.string(),
                paste: z.string(),
                'phone-hidden': z.string(),
                planned: z.string(),
                private: z.string(),
                profile: z.string(),
                'read-more': z.string(),
                reject: z.string(),
                reload: z.string(),
                remove: z.string(),
                reset: z.string(),
                save: z.string(),
                search: z.string(),
                'select-all': z.string(),
                settings: z.string(),
                setup: z.string(),
                shared: z.string(),
                'show-all': z.string(),
                'show-less': z.string(),
                'show-more': z.string(),
                'sign-in': z.string(),
                skill: z.string(),
                'skills-unavailable': z.string(),
                skip: z.string(),
                start: z.string(),
                'start-audio-transcription': z.string(),
                status: z.string(),
                'stop-recording': z.string(),
                submit: z.string(),
                title: z.string(),
                to: z.string(),
                today: z.string(),
                'user-name-invisible': z.string(),
                'user-name-unavailable': z.string(),
                welcome: z.string(),
                yes: z.string(),
                yesterday: z.string(),
                your: z.string(),
            })
            .partial(),
        'company-onboarding': z
            .object({
                'profile-introduction': z.string(),
                'profile-introduction-description': z.string(),
                'provide-your-data': z
                    .object({
                        headline: z.string(),
                        'job-title': z.string(),
                        'job-title-placeholder': z.string(),
                        subheadline: z.string(),
                    })
                    .partial(),
            })
            .partial(),
        components: z
            .object({
                'add-to-ambitions-button': z.string(),
                'add-to-profile-button': z.string(),
                'anonymize-button': z
                    .object({
                        'user-names-hide': z.string(),
                        'user-names-show': z.string(),
                    })
                    .partial(),
                'data-grid-panel': z
                    .object({
                        footer: z
                            .object({
                                'hide-all': z.string(),
                                'show-all': z.string(),
                            })
                            .partial(),
                        title: z.string(),
                        'title-placeholder': z.string(),
                    })
                    .partial(),
                'form-date-picker-field': z
                    .object({
                        clear: z.string(),
                        'next-month': z.string(),
                        'previous-month': z.string(),
                        today: z.string(),
                    })
                    .partial(),
                'invisible-indicator': z.string(),
                'learning-opportunity-card': z
                    .object({
                        bookmark: z.string(),
                        caption: z.string(),
                        featured: z.string(),
                        'featured-by': z.string(),
                        'skill-message': z.string(),
                    })
                    .partial(),
                'location-filter': z
                    .object({
                        city: z.string(),
                        region: z.string(),
                    })
                    .partial(),
                'remove-from-ambitions-button': z.string(),
                skills: z
                    .object({
                        added: z.string(),
                        'added-language-skills': z.string(),
                        'added-skills': z.string(),
                        'no-skills-attached': z.string(),
                        'no-skills-found': z.string(),
                        removed: z.string(),
                        'search-placeholder': z.string(),
                        'show-skill-recommendation': z.string(),
                        'suggested-hard-skills': z.string(),
                        'suggested-language-skills': z.string(),
                        'suggested-skills': z.string(),
                        'suggested-soft-skills': z.string(),
                    })
                    .partial(),
                'skills-detection': z
                    .object({
                        'button-label': z.string(),
                        'caption-disabled': z.string(),
                        'caption-enabled': z.string(),
                    })
                    .partial(),
                'skills-field': z
                    .object({
                        name: z.string(),
                    })
                    .partial(),
                'take-course': z.string(),
            })
            .partial(),
        customization: z
            .object({
                alert: z.string(),
                reset: z.string(),
            })
            .partial(),
        dialogs: z
            .object({
                buttons: z
                    .object({
                        cancel: z.string(),
                        save: z.string(),
                    })
                    .partial(),
                common: z
                    .object({
                        'not-revertible-action-warn': z.string(),
                    })
                    .partial(),
                delete: z
                    .object({
                        'certificate-confirm-title': z.string(),
                        'course-confirm-title': z.string(),
                        'education-confirm-title': z.string(),
                        'job-profile-confirm-text': z.string(),
                        'job-profile-confirm-title': z.string(),
                        'project-confirm-title': z.string(),
                        'publication-confirm-title': z.string(),
                        'work-confirm-title': z.string(),
                    })
                    .partial(),
                discard: z
                    .object({
                        content: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'general-information': z
                    .object({
                        location: z.string(),
                    })
                    .partial(),
                job: z
                    .object({
                        status: z.string(),
                        'title-delete': z.string(),
                    })
                    .partial(),
                'language-select-disabled-tooltip': z.string(),
                'learning-opportunities': z
                    .object({
                        'search-placeholder': z.string(),
                        text: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'learning-opportunity': z
                    .object({
                        'exclude-featured-users': z.string(),
                        'title-delete': z.string(),
                    })
                    .partial(),
                'no-consent': z
                    .object({
                        back: z.string(),
                        content: z.string(),
                        delete: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'shortlist-notes': z
                    .object({
                        'add-note': z.string(),
                        'add-note-anonymized': z.string(),
                        'add-note-placeholder': z.string(),
                    })
                    .partial(),
                skills: z
                    .object({
                        proficiency: z
                            .object({
                                language: z
                                    .object({
                                        0: z.string(),
                                        1: z.string(),
                                        2: z.string(),
                                        3: z.string(),
                                        4: z.string(),
                                        5: z.string(),
                                        6: z.string(),
                                    })
                                    .partial(),
                                skills: z
                                    .object({
                                        0: z.string(),
                                        1: z.string(),
                                        2: z.string(),
                                        3: z.string(),
                                        4: z.string(),
                                        5: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                    })
                    .partial(),
                terms: z
                    .object({
                        'consent-label': z.string(),
                        'consent-label-first-part': z.string(),
                        'consent-label-second-part': z.string(),
                        'last-updated': z.string(),
                        'load-error': z.string(),
                        loading: z.string(),
                        proceed: z.string(),
                        title: z.string(),
                        'validation-message': z.string(),
                    })
                    .partial(),
                'update-user-profile-skills': z
                    .object({
                        'agree-update-profile-skills': z.string(),
                        headline: z.string(),
                        'limit-exceed-agree': z.string(),
                        'limit-text': z.string(),
                        'reject-update-profile-skills': z.string(),
                        'update-skills-text': z.string(),
                    })
                    .partial(),
                'user-settings': z
                    .object({
                        'account-deletion-message': z.string(),
                        'account-logged-out': z.string(),
                        'delete-account': z.string(),
                        'no-i-want-to-stay': z.string(),
                        'see-all-translations': z.string(),
                    })
                    .partial(),
            })
            .partial(),
        errors: z
            .object({
                common: errorTranslationSchema,
                component: z
                    .object({
                        dashboard: z
                            .object({
                                summary: z
                                    .object({
                                        ambitions: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        drawer: z
                            .object({
                                ambitions: z.string(),
                                'candidate-matching-info': z.string(),
                                languages: z.string(),
                                'profile-preview': z.string(),
                                skills: z.string(),
                                'skills-detail': z.string(),
                            })
                            .partial(),
                        'featured-opportunities': z.string(),
                        'global-search': z
                            .object({
                                employees: z.string(),
                                'job-openings': z.string(),
                                'learning-opportunities': z.string(),
                                skills: z.string(),
                            })
                            .partial(),
                        job: z
                            .object({
                                'candidate-recommendations-section': z.string(),
                                'candidate-shortlist-section': z.string(),
                                create: z.string(),
                                edit: z.string(),
                                'job-matching-section': z.string(),
                                'job-skills': z.string(),
                                'update-ambitions': z.string(),
                            })
                            .partial(),
                        'job-profiles': z
                            .object({
                                'job-profile-created': z.string(),
                            })
                            .partial(),
                        'job-recommendation-list-content': z.string(),
                        'job-recommendations-filter': z.string(),
                        'learning-opportunities': z
                            .object({
                                'add-to-profile': z.string(),
                                create: z.string(),
                                description: z.string(),
                                edit: z.string(),
                                'featured-users': z.string(),
                                skills: z.string(),
                            })
                            .partial(),
                        profile: z
                            .object({
                                ambitions: z.string(),
                                'certificates-history': z.string(),
                                'course-history': z.string(),
                                'education-history': z.string(),
                                languages: z.string(),
                                'project-history': z.string(),
                                'publication-history': z.string(),
                                skills: z.string(),
                                'skills-suggestions': z.string(),
                                'work-history': z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                fatal: z
                    .object({
                        message: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                network: z
                    .object({
                        message: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'required-fields-empty': z.string(),
                status: z.record(httpStatusCodeSchema.transform(String), errorTranslationSchema),
            })
            .partial(),
        globalsearch: z
            .object({
                'exact-skills': z.string(),
                jobs: z.string(),
                'learning-opportunities': z.string(),
                'placeholder-min-length': z.string(),
                'placeholder-multiple-skills': z.string(),
                'placeholder-to-search': z.string(),
                'related-skills': z.string(),
                'see-more': z.string(),
                skills: z.string(),
                'skills-failed': z.string(),
                users: z.string(),
            })
            .partial(),
        navigation: z
            .object({
                'alignment-record': z.string(),
                'analytics-dashboard': z.string(),
                'company-settings': z.string(),
                'control-room': z.string(),
                dashboard: z.string(),
                'job-architecture': z.string(),
                'job-management': z.string(),
                'job-postings': z.string(),
                'job-profiles': z.string(),
                'learning-management': z.string(),
                'my-alignment': z.string(),
                'my-growth': z.string(),
                'position-management': z.string(),
                profile: z.string(),
                'talent-directory': z.string(),
            })
            .partial(),
        pages: z
            .object({
                'alignment-record': z
                    .object({
                        'accept-suggested-focus-skills-description': z.string(),
                        'activity-date': z.string(),
                        'add-activity': z.string(),
                        'add-as-focus-skills': z.string(),
                        'add-focus-skill': z.string(),
                        'added-on': z.string(),
                        alignment: z.string(),
                        'alignment-activities': z.string(),
                        'alignment-record': z.string(),
                        'confirm-and-continue': z.string(),
                        context: z.string(),
                        documentState: z.string(),
                        documentStateValue: z
                            .object({
                                active: z.string(),
                                archived: z.string(),
                            })
                            .partial(),
                        due: z.string(),
                        'edit-activity': z.string(),
                        employee: z.string(),
                        'employee-confirmed': z.string(),
                        'employee-not-confirmed': z.string(),
                        'focus-skill-used': z.string(),
                        'focus-skills': z.string(),
                        'focus-skills-notification-description': z.string(),
                        'focus-skills-notification-title': z.string(),
                        guide: z.string(),
                        introduction: z.string(),
                        'introduction-passions': z
                            .object({
                                subtitle: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'introduction-position': z
                            .object({
                                subtitle: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'introduction-skills': z
                            .object({
                                subtitle: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'introduction-subtitle': z.string(),
                        'introduction-title': z.string(),
                        lastModified: z.string(),
                        manager: z.string(),
                        'manager-confirmed': z.string(),
                        'manager-not-confirmed': z.string(),
                        modifiedBy: z.string(),
                        'operational-goals-description': z.string(),
                        'operational-goals-title': z.string(),
                        'passions-and-aspirations': z.string(),
                        'personal-goals-description': z.string(),
                        'personal-goals-title': z.string(),
                        planned: z.string(),
                        position: z.string(),
                        'position-notification-description': z.string(),
                        'position-notification-title': z.string(),
                        'position-review': z.string(),
                        'purpose-notification-description': z.string(),
                        'purpose-notification-title': z.string(),
                        'regenerate-focus-skill-suggestions': z.string(),
                        'request-meeting-with-manager': z.string(),
                        'request-meeting-with-manager-email': z
                            .object({
                                greeting: z.string(),
                                message: z.string(),
                                signature: z.string(),
                                subject: z.string(),
                            })
                            .partial(),
                        'required-skills': z.string(),
                        'search-skills-placeholder': z.string(),
                        'suggested-focus-skills': z.string(),
                        title: z.string(),
                        'type-here': z.string(),
                        'visible-to-manager': z.string(),
                    })
                    .partial(),
                'business-intelligence': z
                    .object({
                        innovation: z
                            .object({
                                text: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'skill-gap': z
                            .object({
                                average: z.string(),
                                options: z
                                    .object({
                                        monthly: z.string(),
                                        quarterly: z.string(),
                                        yearly: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'skill-groups': z
                            .object({
                                title: z.string(),
                            })
                            .partial(),
                        'trending-jobs': z
                            .object({
                                clicks: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'trending-skills': z
                            .object({
                                impressions: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        visibility: z
                            .object({
                                'anonymous-profiles': z.string(),
                                'public-profiles': z.string(),
                                title: z.string(),
                                'total-employees': z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                'company-onboarding': z
                    .object({
                        'company-description': z.string(),
                        'company-description-subtitle': z.string(),
                        'company-description-title': z.string(),
                        'company-priorities': z.string(),
                        'company-priorities-subtitle': z.string(),
                        'company-priorities-title': z.string(),
                        'company-profile': z.string(),
                        dashboard: z.string(),
                        'employee-profile': z.string(),
                        'invite-your-team': z.string(),
                        'provide-your-data': z.string(),
                        'provide-your-data-subtitle': z.string(),
                        'provide-your-data-title': z.string(),
                        'review-skill-mosaic': z.string(),
                        'review-skill-profile': z.string(),
                        'review-skill-profile-subtitle': z.string(),
                        'review-skill-profile-title': z.string(),
                        'setup-cobrainer': z.string(),
                        'skill-mosaic': z.string(),
                    })
                    .partial(),
                'company-settings': z
                    .object({
                        'danger-zone': z
                            .object({
                                'delete-button': z.string(),
                                'delete-card-description': z.string(),
                                'delete-card-title': z.string(),
                                'delete-dialog-description': z.string(),
                                'delete-dialog-title': z.string(),
                                subtitle: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        description: z.string(),
                        'discard-changes-description': z.string(),
                        'discard-changes-description-conditional': z.string(),
                        'discard-changes-title': z.string(),
                        'failed-to-delete': z.string(),
                        'failed-to-save': z.string(),
                        fields: z
                            .object({
                                'company-description': z
                                    .object({
                                        empty: z.string(),
                                        label: z.string(),
                                        placeholder: z.string(),
                                    })
                                    .partial(),
                                'company-name': z
                                    .object({
                                        empty: z.string(),
                                        label: z.string(),
                                        placeholder: z.string(),
                                        required: z.string(),
                                    })
                                    .partial(),
                                'company-size': z
                                    .object({
                                        empty: z.string(),
                                        label: z.string(),
                                        options: z
                                            .object({
                                                gigantic: z.string(),
                                                huge: z.string(),
                                                large: z.string(),
                                                medium: z.string(),
                                                small: z.string(),
                                            })
                                            .partial(),
                                        placeholder: z.string(),
                                        required: z.string(),
                                    })
                                    .partial(),
                                'job-architecture-name': z
                                    .object({
                                        empty: z.string(),
                                        label: z.string(),
                                        placeholder: z.string(),
                                        required: z.string(),
                                    })
                                    .partial(),
                                jsa: z
                                    .object({
                                        placeholder: z.string(),
                                    })
                                    .partial(),
                                'top-priorities': z
                                    .object({
                                        empty: z.string(),
                                        label: z.string(),
                                        placeholder: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'job-architecture-description': z.string(),
                        'job-architecture-title': z.string(),
                        'keep-editing': z.string(),
                        'saved-successfully': z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'cv-reimport': z
                    .object({
                        headline: z.string(),
                        preview: z
                            .object({
                                description: z.string(),
                                save: z.string(),
                                'sub-headline': z.string(),
                            })
                            .partial(),
                        result: z
                            .object({
                                continue: z.string(),
                                'found-items': z.string(),
                                'merge-profile': z.string(),
                                'override-profile': z.string(),
                                prompt: z.string(),
                                'sub-headline': z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                dashboard: z
                    .object({
                        ambitions: z
                            .object({
                                'add-selected-ambitions-as-my-ambitions': z.string(),
                                'edit-button-label': z.string(),
                                'info-box': z
                                    .object({
                                        'first-paragraph': z.string(),
                                        'no-ambitions-button-label': z.string(),
                                        'no-ambitions-first-paragraph': z.string(),
                                        'no-ambitions-second-paragraph': z.string(),
                                        'second-paragraph': z.string(),
                                    })
                                    .partial(),
                                'see-courses': z.string(),
                                'tab-label': z.string(),
                            })
                            .partial(),
                        'dashboard-card': z
                            .object({
                                'matching-score': z.string(),
                                new: z.string(),
                                'relevant-missing-skill': z.string(),
                                'relevant-skill': z.string(),
                            })
                            .partial(),
                        'featured-learning-opportunities': z
                            .object({
                                subtitle: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        headline: z.string(),
                        'job-recommendations': z
                            .object({
                                filters: z
                                    .object({
                                        'sub-title': z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                headline: z.string(),
                                'headline-short': z.string(),
                                'job-card': z
                                    .object({
                                        type: z.string(),
                                    })
                                    .partial(),
                                'no-jobs': z
                                    .object({
                                        'no-recommendations': z
                                            .object({
                                                headline: z.string(),
                                                link: z.string(),
                                                text: z.string(),
                                            })
                                            .partial(),
                                        'not-enough-skills': z
                                            .object({
                                                headline: z.string(),
                                                link: z.string(),
                                                text: z.string(),
                                            })
                                            .partial(),
                                    })
                                    .partial(),
                                'show-more': z.string(),
                                'skills-based-sub-headline': z.string(),
                                'sub-headline': z.string(),
                            })
                            .partial(),
                        'learning-opportunity-recommendations': z
                            .object({
                                'empty-results': z.string(),
                                headline: z.string(),
                                'show-more': z.string(),
                                'sub-headline': z.string(),
                                subtitle: z.string(),
                                title: z.string(),
                                'view-all': z.string(),
                            })
                            .partial(),
                        'welcome-back': z.string(),
                    })
                    .partial(),
                'denied-page': z
                    .object({
                        subtitle: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'edit-about-me': z
                    .object({
                        'no-about': z.string(),
                        placeholder: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'edit-contact-details': z
                    .object({
                        email: z.string(),
                        phone: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'edit-personal-info': z
                    .object({
                        'job-seeking': z.string(),
                        'relocation-preference': z.string(),
                        'seniority-level': z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'edit-profile-details': z
                    .object({
                        department: z.string(),
                        'job-title': z.string(),
                        location: z.string(),
                        phone: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                feedback: z
                    .object({
                        'change-skill-language': z.string(),
                        'change-skill-reason': z.string(),
                        'change-skill-reason-placeholder': z.string(),
                        'create-skill-placeholder': z.string(),
                        'create-skill-skill-type': z.string(),
                        'feedback-description': z.string(),
                        'provide-feedback-description': z.string(),
                        'provide-feedback-title': z.string(),
                        'report-content-issue': z.string(),
                        'report-skill': z.string(),
                        'report-skill-reason': z.string(),
                        'report-skill-reason-placeholder': z.string(),
                        'request-new-skill': z.string(),
                        'request-new-skill-title-placeholder': z.string(),
                        'skill-content-report-success-message': z.string(),
                        'skill-content-report-success-title': z.string(),
                        'skill-report-success-message': z.string(),
                        'skill-report-success-title': z.string(),
                        'skill-request-success-message': z.string(),
                        'skill-request-success-title': z.string(),
                        'submit-feedback': z.string(),
                    })
                    .partial(),
                'global-search': z
                    .object({
                        headlines: z
                            .object({
                                courses: z.string(),
                                created: z.string(),
                                'employee-name': z.string(),
                                'employee-skills': z.string(),
                                employees: z.string(),
                                'job-postings': z.string(),
                                jobs: z.string(),
                                'learning-opportunities': z.string(),
                                published: z.string(),
                                'required-skills': z.string(),
                                skill: z.string(),
                                'skill-gap-relative': z.string(),
                                'trained-skills': z.string(),
                            })
                            .partial(),
                        navigation: z
                            .object({
                                employees: z.string(),
                                'job-openings': z.string(),
                                'learning-opportunities': z.string(),
                                skills: z.string(),
                            })
                            .partial(),
                        'no-data': z
                            .object({
                                employees: z.string(),
                                'job-openings': z.string(),
                                'learning-opportunities': z.string(),
                                skills: z.string(),
                            })
                            .partial(),
                        placeholder: z.string(),
                        'search-headline': z.string(),
                    })
                    .partial(),
                home: z
                    .object({
                        aligned: z.string(),
                        alignment: z.string(),
                        'coming-soon': z.string(),
                        'define-company-structure': z.string(),
                        'define-purpose': z.string(),
                        'get-started': z.string(),
                        hire: z.string(),
                        'invite-team-members': z.string(),
                        'not-aligned': z.string(),
                        'required-to-invite-team-members': z.string(),
                        'required-to-see-company-alignment': z.string(),
                        'team-size': z.string(),
                        uncertain: z.string(),
                        'welcome-message': z.string(),
                    })
                    .partial(),
                'invitation-dialog': z
                    .object({
                        'add-position': z.string(),
                        'invitation-success-message': z.string(),
                        'invite-button': z.string(),
                        position: z.string(),
                        subtitle: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'job-architecture': z
                    .object({
                        'adjust-job-profile': z.string(),
                        'adjust-with-ai': z.string(),
                        'apply-changes-to-all-languages-label': z.string(),
                        'assign-job-profile': z.string(),
                        'automatically-select-career-track': z.string(),
                        'automatically-select-career-track-description': z.string(),
                        'blue-collar-label': z.string(),
                        'career-track-add-label': z.string(),
                        'career-track-headline-label': z.string(),
                        'career-track-label': z.string(),
                        'cobrainer-default-leveling-label': z.string(),
                        'company-profile-settings-career-framework-approach-description': z.string(),
                        'company-profile-settings-career-framework-approach-title': z.string(),
                        'company-profile-settings-career-framework-create-own-subtitle': z.string(),
                        'company-profile-settings-career-framework-create-own-title': z.string(),
                        'company-profile-settings-career-framework-predefined-subtitle': z.string(),
                        'company-profile-settings-career-framework-predefined-title': z.string(),
                        'company-profile-settings-company-description-hint': z.string(),
                        'company-profile-settings-company-description-title': z.string(),
                        'company-profile-settings-company-details': z.string(),
                        'company-profile-settings-company-details-description': z.string(),
                        'company-profile-settings-company-name-description': z.string(),
                        'company-profile-settings-company-name-title': z.string(),
                        'company-profile-settings-company-priorities-description': z.string(),
                        'company-profile-settings-company-priorities-title': z.string(),
                        'company-profile-settings-executive-roles-description': z.string(),
                        'company-profile-settings-executive-roles-title': z.string(),
                        'company-profile-settings-jsa-hint': z.string(),
                        'company-profile-settings-jsa-title': z.string(),
                        'company-profile-settings-number-of-employees-description': z.string(),
                        'company-profile-settings-number-of-employees-title': z.string(),
                        'company-size-number-placeholder': z.string(),
                        'create-job-architecture-button-label': z.string(),
                        'create-job-levels': z.string(),
                        'create-manually': z.string(),
                        'created-by': z.string(),
                        'created-success-job-architecture-button-label': z.string(),
                        'custom-leveling-framework-label': z.string(),
                        'delete-career-track-confirmation-dialog-description': z.string(),
                        'delete-confirmation-dialog-delete': z.string(),
                        'delete-confirmation-dialog-description': z.string(),
                        'delete-confirmation-dialog-discard': z.string(),
                        'delete-confirmation-dialog-title': z.string(),
                        'delete-job-architecture-button-label': z.string(),
                        'delete-job-architecture-dialog-description': z.string(),
                        'delete-job-architecture-dialog-title': z.string(),
                        'delete-job-architecture-label': z.string(),
                        'describe-ai-changes': z.string(),
                        description: z.string(),
                        'employees-with-this-skill': z.string(),
                        'enter-job-profile-title': z.string(),
                        'enter-valid-title': z.string(),
                        'executive-roles-in-job-levels': z.string(),
                        'executive-roles-in-own-job-family': z.string(),
                        'export-button': z.string(),
                        'export-error': z.string(),
                        'export-pdf-button': z.string(),
                        'export-pdf-disabled-hint': z.string(),
                        'export-pdf-error': z.string(),
                        'export-title': z.string(),
                        'generate-job-levels': z.string(),
                        'generate-with-ai': z.string(),
                        'generate-with-ai-dialog-description': z.string(),
                        'generate-with-ai-dialog-subtitle': z.string(),
                        'generate-with-ai-dialog-title': z.string(),
                        'generate-with-ai-setup-dialog-final-step-description': z.string(),
                        'generate-with-ai-setup-dialog-final-step-title': z.string(),
                        'job-cluster': z.string(),
                        'job-family': z.string(),
                        'job-level': z.string(),
                        'job-level-career-track-default-setting': z.string(),
                        'job-level-generate-with-ai-settings-dialog-career-track-selection-title': z.string(),
                        'job-level-generate-with-ai-settings-dialog-select-auto-subtitle': z.string(),
                        'job-level-generate-with-ai-settings-dialog-select-auto-title': z.string(),
                        'job-level-generate-with-ai-settings-dialog-select-auto-with-content-sub-subtitle': z.string(),
                        'job-level-generate-with-ai-settings-dialog-select-auto-with-skills-sub-subtitle': z.string(),
                        'job-level-generate-with-ai-settings-dialog-select-auto-with-skills-subtitle': z.string(),
                        'job-level-generate-with-ai-settings-dialog-select-auto-with-skills-title': z.string(),
                        'job-level-generate-with-ai-settings-dialog-select-manual-subtitle': z.string(),
                        'job-level-generate-with-ai-settings-dialog-select-manual-title': z.string(),
                        'job-level-generate-with-ai-settings-dialog-title': z.string(),
                        'job-levels-add-level-label': z.string(),
                        'job-levels-label': z.string(),
                        'job-profile': z.string(),
                        'job-profile-accept-changes': z.string(),
                        'job-profile-empty-competency-placeholder': z.string(),
                        'job-profile-generate-entire-description-button-label': z.string(),
                        'job-profile-generic-error': z.string(),
                        'job-profile-popup-description-generate-button-label': z.string(),
                        'job-profile-popup-description-generate-placeholder-label': z.string(),
                        'job-profile-popup-headline': z.string(),
                        'job-profile-redetect-all-skills': z.string(),
                        'job-profile-redetect-all-skills-placeholder': z.string(),
                        'job-profile-reject-changes': z.string(),
                        'job-profile-skill-no-suggestions-error': z.string(),
                        'job-profile-skill-search-placeholder-label': z.string(),
                        'job-profile-skill-suggestions-add-label': z.string(),
                        'job-profile-skill-suggestions-adjust-label': z.string(),
                        'job-profile-skill-suggestions-failed-error': z.string(),
                        'job-profile-skill-suggestions-remove-label': z.string(),
                        'job-profile-title-duplicate-error': z.string(),
                        'job-profile-title-invalid-error': z.string(),
                        'job-profile-validation-error': z.string(),
                        'job-profiles-with-this-skill': z.string(),
                        'job-role': z.string(),
                        'jobs-with-this-skill': z.string(),
                        'jsa-settings-drawer-label': z.string(),
                        'last-modified': z.string(),
                        'mark-as-reviewed': z.string(),
                        'market-based-leveling-framework-label': z.string(),
                        'new-job-architecture': z.string(),
                        'new-job-architecture-dialog-description': z.string(),
                        'new-job-architecture-dialog-title': z.string(),
                        'no-job-level-frameworks-existing': z.string(),
                        'not-assigned': z.string(),
                        'not-verified': z.string(),
                        'proficiency-dropdown-placeholder': z.string(),
                        'redetect-skills': z.string(),
                        'redirect-to-job-profile-dialog-cancel': z.string(),
                        'redirect-to-job-profile-dialog-continue': z.string(),
                        'redirect-to-job-profile-dialog-description': z.string(),
                        'redirect-to-job-profile-dialog-title': z.string(),
                        'regenerate-job-profile': z.string(),
                        'remove-competency': z.string(),
                        'required-skills': z.string(),
                        revert: z.string(),
                        'review-by': z.string(),
                        rewrite: z.string(),
                        'select-a-job-architecture': z.string(),
                        'select-a-job-cluster': z.string(),
                        'select-a-job-family': z.string(),
                        'select-a-job-level': z.string(),
                        'select-a-job-role': z.string(),
                        'select-a-skill-cluster': z.string(),
                        'select-a-skill-family': z.string(),
                        'select-job-architecture': z.string(),
                        'select-trade-unions-dialog-title': z.string(),
                        'select-trade-unions-hint-label': z.string(),
                        'skill-cluster': z.string(),
                        'skill-family': z.string(),
                        'skill-limit-exceeded-error': z.string(),
                        skills: z.string(),
                        'sort-alphabetically': z.string(),
                        'tab-slider-jobs': z.string(),
                        'tab-slider-skills': z.string(),
                        title: z.string(),
                        'trade-union': z
                            .object({
                                djv: z.string(),
                                evg: z.string(),
                                gdl: z.string(),
                                gew: z.string(),
                                ig_bau: z.string(),
                                ig_bce: z.string(),
                                ig_metall: z.string(),
                                marburger_bund: z.string(),
                                market_based: z.string(),
                                ngg: z.string(),
                                ufo: z.string(),
                                verdi: z.string(),
                            })
                            .partial(),
                        'type-here': z.string(),
                        'union-based-leveling-framework-label': z.string(),
                        verified: z.string(),
                        'verified-status': z.string(),
                        'verify-job-profile': z.string(),
                        'white-collar-label': z.string(),
                    })
                    .partial(),
                'job-architecture-v2': z
                    .object({
                        'add-career-track-button': z.string(),
                        'add-competency-button': z.string(),
                        'add-level-button': z.string(),
                        'adjust-with-ai': z.string(),
                        'an-error-occurred': z.string(),
                        'career-framework-title': z.string(),
                        'career-tracks-header': z.string(),
                        'change-proficiency-level': z.string(),
                        changelog: z.object({
                            actions: z
                                .object({
                                    'assigned-reviewer': z.string(),
                                    copied: z.string(),
                                    created: z.string(),
                                    deleted: z.string(),
                                    edited: z.string(),
                                    moved: z.string(),
                                    published: z.string(),
                                    reviewed: z.string(),
                                    'unassigned-reviewer': z.string(),
                                })
                                .partial(),
                            'entity-types': z
                                .object({
                                    root: z.string(),
                                })
                                .partial(),
                            from: z.string(),
                            in: z.string(),
                            'no-entries': z.string(),
                            of: z.string(),
                            subtitle: z.string(),
                            title: z.string(),
                            to: z.string(),
                            today: z.string(),
                            'unknown-user': z.string(),
                            version: z.string(),
                            yesterday: z.string(),
                        }),
                        'collapse-all': z.string(),
                        comparision: z.object({
                            'title-section': z.string(),
                        }),
                        'complete-setup': z.string(),
                        'create-family-and-clusters': z.string(),
                        'create-manually': z.string(),
                        'create-new': z.string(),
                        'create-only-families': z.string(),
                        'create-whole-architecture': z.string(),
                        'cut-disable-due-sap-integration': z.string(),
                        'delete-job-cluster': z.string(),
                        'delete-job-cluster-description': z.string(),
                        'delete-job-clusters-description': z.string(),
                        'delete-job-families-description': z.string(),
                        'delete-job-family': z.string(),
                        'delete-job-family-description': z.string(),
                        'delete-job-level-description': z.string(),
                        'delete-job-levels-description': z.string(),
                        'delete-job-roles-description': z.string(),
                        'delete-leveling-framework-dialog-description': z.string(),
                        'delete-leveling-framework-dialog-title': z.string(),
                        'delete-track-dialog-description': z.string(),
                        'delete-track-dialog-title': z.string(),
                        'edit-cluster': z.string(),
                        'edit-family': z.string(),
                        'edit-form-description-placeholder': z.string(),
                        'edit-form-title-min-length-error': z.string(),
                        'edit-form-title-placeholder': z.string(),
                        'edit-form-title-required-error': z.string(),
                        'empty-cell-placeholder': z.string(),
                        'expand-all': z.string(),
                        'general-settings': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'generate-all-job-roles-with-ai': z.string(),
                        'generate-job-clusters-with-ai': z.string(),
                        'generate-levels-dialog': z.object({
                            'automatic-description': z.string(),
                            'automatic-title': z.string(),
                            'available-frameworks': z.string(),
                            'create-button': z.string(),
                            'custom-framework-description': z.string(),
                            'custom-framework-title': z.string(),
                            'default-framework-description': z.string(),
                            'default-framework-title': z.string(),
                            description: z.string(),
                            'generate-button': z.string(),
                            'manual-description': z.string(),
                            'manual-title': z.string(),
                            'requires-setup': z.string(),
                            'select-framework-button': z.string(),
                            'setup-frameworks-button': z.string(),
                            title: z.string(),
                            'title-multiple-roles': z.string(),
                        }),
                        'generate-with-ai': z.string(),
                        'generate-with-ai-warning': z.string(),
                        'generate-with-ai-warning-description': z.string(),
                        'generation-started-cta': z.string(),
                        'generation-started-description': z.string(),
                        'generation-started-title': z.string(),
                        'in-flight': z.object({
                            'cluster-generations': z.string(),
                            'family-generations': z.string(),
                            'level-generations': z.string(),
                            'processing-architecture': z.string(),
                            'role-generations': z.string(),
                            'skill-detections': z.string(),
                        }),
                        'job-add-reviewer': z.string(),
                        'job-add-reviewer-notification': z.string(),
                        'job-assign-reviewer': z.string(),
                        'job-cluster': z.string(),
                        'job-cluster-description': z.string(),
                        'job-cluster-description-placeholder': z.string(),
                        'job-cluster-name-placeholder': z.string(),
                        'job-clusters': z.string(),
                        'job-families': z.string(),
                        'job-families-generate-with-ai': z.string(),
                        'job-family': z.string(),
                        'job-family-description': z.string(),
                        'job-family-description-placeholder': z.string(),
                        'job-family-description-with-families': z.string(),
                        'job-family-explanation': z.string(),
                        'job-family-name-placeholder': z.string(),
                        'job-level': z.string(),
                        'job-level-edit': z.string(),
                        'job-level-view': z.string(),
                        'job-levels': z.string(),
                        'job-levels-enter-level-name': z.string(),
                        'job-levels-generate-all-with-ai': z.string(),
                        'job-levels-generate-with-ai': z.string(),
                        'job-levels-title-placeholder': z.string(),
                        'job-profile-accept-changes': z.string(),
                        'job-profile-compare-versions': z.string(),
                        'job-profile-competency-search-placeholder': z.string(),
                        'job-profile-created-by': z.string(),
                        'job-profile-error-prefix': z.string(),
                        'job-profile-generating-notification': z.string(),
                        'job-profile-last-modified': z.string(),
                        'job-profile-loading': z.string(),
                        'job-profile-new-version': z.string(),
                        'job-profile-no-data-available': z.string(),
                        'job-profile-no-skill-level': z.string(),
                        'job-profile-regenerate': z.string(),
                        'job-profile-reject-changes': z.string(),
                        'job-profile-required-skills': z.string(),
                        'job-profile-reviewer-label': z.string(),
                        'job-profile-search-placeholder': z.string(),
                        'job-profile-skill-level': z.string(),
                        'job-profile-skill-search-placeholder': z.string(),
                        'job-profile-skill-suggestions-add-label': z.string(),
                        'job-profile-skill-suggestions-adjust-skills-label': z.string(),
                        'job-profile-skill-suggestions-remove-label': z.string(),
                        'job-profile-status-approved': z.string(),
                        'job-profile-status-assigned': z.string(),
                        'job-profile-status-draft': z.string(),
                        'job-profile-status-published': z.string(),
                        'job-profile-status-reviewed': z.string(),
                        'job-profile-title': z.string(),
                        'job-profile-unknown-user': z.string(),
                        'job-profile-updates-available': z.string(),
                        'job-profile-version': z.string(),
                        'job-profile-version-placeholder': z.string(),
                        'job-ready-to-publish': z.string(),
                        'job-ready-to-review': z.string(),
                        'job-role': z.string(),
                        'job-role-description': z.string(),
                        'job-role-description-with-roles': z.string(),
                        'job-role-publish-button': z.string(),
                        'job-role-publish-description': z.string(),
                        'job-role-publish-new-version-notification': z.string(),
                        'job-role-publish-notification': z.string(),
                        'job-role-publish-title': z.string(),
                        'job-role-review-new-version-notification': z.string(),
                        'job-role-review-notification': z.string(),
                        'job-roles': z.string(),
                        'job-roles-add-level': z.string(),
                        'job-roles-and-levels': z.string(),
                        'job-roles-delete-confirmation': z.string(),
                        'job-roles-delete-title': z.string(),
                        'job-roles-edit-role': z.string(),
                        'job-roles-enter-role-name': z.string(),
                        'job-roles-error-occurred': z.string(),
                        'job-roles-generate-all-with-ai': z.string(),
                        'job-roles-generate-levels-with-ai': z.string(),
                        'job-roles-generate-with-ai': z.string(),
                        'job-roles-profiles': z.string(),
                        'job-roles-reviewed': z.string(),
                        'job-roles-reviewers': z.string(),
                        'job-roles-view-role': z.string(),
                        'job-unassigned': z.string(),
                        'leveling-framework-create-button': z.string(),
                        'leveling-framework-create-button-from-template': z.string(),
                        'leveling-framework-create-title': z.string(),
                        'leveling-framework-description-label': z.string(),
                        'leveling-framework-description-placeholder': z.string(),
                        'leveling-framework-edit-title': z.string(),
                        'leveling-framework-label': z.string(),
                        'leveling-framework-list-description': z.string(),
                        'leveling-framework-list-empty-state': z.string(),
                        'leveling-framework-list-loading': z.string(),
                        'leveling-framework-load-error': z.string(),
                        'leveling-framework-loading': z.string(),
                        'leveling-framework-name-label': z.string(),
                        'leveling-framework-name-min-length': z.string(),
                        'leveling-framework-name-placeholder': z.string(),
                        'leveling-framework-name-required': z.string(),
                        'leveling-framework-non-deletable-tooltip': z.string(),
                        'leveling-framework-non-editable-tooltip': z.string(),
                        'leveling-framework-predefined-activate-button': z.string(),
                        'leveling-framework-predefined-dialog-title': z.string(),
                        'leveling-framework-predefined-empty': z.string(),
                        'leveling-framework-predefined-selected': z.string(),
                        'leveling-framework-saving': z.string(),
                        'leveling-framework-use-predefined-button': z.string(),
                        levels: z.string(),
                        'manage-career-tracks-and-levels-subtitle': z.string(),
                        'mark-job-role-reviewed-content': z.string(),
                        'mark-job-role-reviewed-title': z.string(),
                        'open-separate-page': z.string(),
                        'regenerate-dialog': z.object({
                            'additional-prompt-placeholder': z.string(),
                            'apply-to-description': z.string(),
                            'apply-to-skills': z.string(),
                            description: z.string(),
                        }),
                        'regenerate-disabled-dirty-form': z.string(),
                        'review-state': z
                            .object({
                                draft: z.string(),
                                'new-version': z.string(),
                                published: z.string(),
                            })
                            .partial(),
                        'search-modal': z
                            .object({
                                'error-description': z.string(),
                                'error-title': z.string(),
                                'filter-reviewer': z.string(),
                                'filter-type': z.string(),
                                'no-results-description': z.string(),
                                'no-results-title': z.string(),
                                'search-placeholder': z.string(),
                            })
                            .partial(),
                        'search-reviewers-placeholder': z.string(),
                        'select-architecture': z.object({
                            placeholder: z.string(),
                        }),
                        settings: z
                            .object({
                                'last-modified': z.string(),
                                'no-architectures': z.object({
                                    description: z.string(),
                                    title: z.string(),
                                }),
                                placeholder: z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                sidebar: z
                                    .object({
                                        'career-framework': z.string(),
                                        'company-profile': z.string(),
                                        general: z.string(),
                                    })
                                    .partial(),
                                tabs: z
                                    .object({
                                        'job-architecture': z.string(),
                                        organization: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'show-skill-details': z.string(),
                        'skill-action-switcher': z.object({
                            'adjust-with-ai': z.string(),
                            'describe-ai-changes': z.string(),
                            search: z.string(),
                            send: z.string(),
                        }),
                        'skill-details-error': z.string(),
                        'skill-details-loading': z.string(),
                        'skill-details-no-data': z.string(),
                        standalone: z.object({
                            'view-in-job-architecture': z.string(),
                        }),
                        statistics: z.object({
                            'level-count': z.string(),
                            'levels-with-reviewers': z.string(),
                            profiles: z.string(),
                            'published-levels': z.string(),
                            'published-roles': z.string(),
                            reviewed: z.string(),
                            'reviewed-levels': z.string(),
                            'reviewed-roles': z.string(),
                            reviewers: z.string(),
                            'role-count': z.string(),
                            'roles-with-reviewers': z.string(),
                        }),
                        suggested: z.string(),
                        'table-column': z.object({
                            level: z.string(),
                            name: z.string(),
                            'published-by': z.string(),
                            reviewer: z.string(),
                            roles: z.string(),
                            status: z.string(),
                            type: z.string(),
                        }),
                        tabs: z.object({
                            board: z.string(),
                            overview: z.string(),
                            'position-management': z.string(),
                            'roles-and-levels': z.string(),
                            skills: z.string(),
                            table: z.string(),
                        }),
                        'unsaved-changes-dialog-description': z.string(),
                        'unsaved-changes-dialog-title': z.string(),
                        'validation-error-skills-missing-proficiency': z.string(),
                        'view-cluster': z.string(),
                        'view-family': z.string(),
                    })
                    .partial(),
                'job-detail': z
                    .object({
                        'additional-skills': z.string(),
                        apply: z.string(),
                        'apply-message': z.string(),
                        'candidate-matching': z
                            .object({
                                description: z.string(),
                                headline: z.string(),
                                matches: z.string(),
                                mismatches: z.string(),
                                'users-missing-skills': z.string(),
                                'users-skills': z.string(),
                            })
                            .partial(),
                        caption: z.string(),
                        contact: z.string(),
                        'contact-person-message': z.string(),
                        'general-info-title': z.string(),
                        greeting: z.string(),
                        job: z.string(),
                        'mail-subject': z.string(),
                        'proficiency-view': z.string(),
                        recommendations: z
                            .object({
                                'clear-all-filters': z.string(),
                                drawer: z
                                    .object({
                                        'action-button-title': z.string(),
                                        'employee-section': z
                                            .object({
                                                headline: z.string(),
                                                'sub-headline': z.string(),
                                            })
                                            .partial(),
                                        headline: z.string(),
                                        'other-employees': z.string(),
                                    })
                                    .partial(),
                                'employee-item': z
                                    .object({
                                        headline: z.string(),
                                        'matching-score-title': z.string(),
                                        'open-for-remote': z.string(),
                                        'skills-headline': z.string(),
                                        'skills-headline-name': z.string(),
                                    })
                                    .partial(),
                                'job-seeking': z
                                    .object({
                                        option: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'load-more': z.string(),
                                'no-candidates': z.string(),
                                'no-recommendations': z
                                    .object({
                                        headline: z.string(),
                                        text: z.string(),
                                    })
                                    .partial(),
                                'not-enough-skills': z
                                    .object({
                                        headline: z.string(),
                                        text: z.string(),
                                    })
                                    .partial(),
                                'relocation-preference': z
                                    .object({
                                        option: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'seniority-level': z.string(),
                                'seniority-level-selected': z.string(),
                                shortlist: z.string(),
                                shortlisted: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'required-skills-title': z.string(),
                        shortlist: z
                            .object({
                                'add-candidate': z.string(),
                                'add-note': z.string(),
                                'edit-note': z.string(),
                                'no-shortlist': z.string(),
                                note: z.string(),
                                'note-anonymized': z.string(),
                                'search-placeholder': z.string(),
                                'shortlisted-by': z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'skill-group-view': z.string(),
                        'skills-recommendation-fields-are-missed': z.string(),
                        'skills-recommendation-tooltip': z.string(),
                        title: z.string(),
                        'user-matching': z
                            .object({
                                description: z.string(),
                                headline: z.string(),
                                match: z.string(),
                                matches: z.string(),
                                'matching-skills': z.string(),
                                mismatches: z.string(),
                                'missing-skills': z.string(),
                                'relevant-learning-opportunities': z.string(),
                                'view-all': z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                'job-posting': z
                    .object({
                        'generate-description-with-ai': z.string(),
                        'generated-description-with-ai-error': z.string(),
                        'generated-description-with-ai-loading-states': z
                            .object({
                                1: z.string(),
                                2: z.string(),
                                3: z.string(),
                                4: z.string(),
                            })
                            .partial(),
                        'generated-description-with-ai-success': z.string(),
                        'generated-description-with-ai-success-title': z.string(),
                        language: z.string(),
                    })
                    .partial(),
                'job-profiles': z
                    .object({
                        assignments: z
                            .object({
                                'add-employee': z.string(),
                                'add-employee-error': z.string(),
                                'add-employee-success': z.string(),
                                'assigned-by': z.string(),
                                filters: z
                                    .object({
                                        'drawer-title': z.string(),
                                        'profile-preferences': z.object({
                                            subtitle: z.string(),
                                            title: z.string(),
                                        }),
                                    })
                                    .partial(),
                                'load-more': z.string(),
                                'remove-assignment': z.string(),
                                'remove-assignment-confirmation': z.string(),
                                'remove-employee-error': z.string(),
                                'remove-employee-success': z.string(),
                                'search-placeholder': z.string(),
                            })
                            .partial(),
                        'create-button': z.string(),
                        'empty-assignment': z
                            .object({
                                subtitle: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        filters: z
                            .object({
                                'review-required-by-me': z.string(),
                                'workspace-preferences': z.object({
                                    'sub-title': z.string(),
                                    title: z.string(),
                                }),
                            })
                            .partial(),
                        headline: z.string(),
                        'no-data': z.string(),
                        'search-placeholder': z.string(),
                        subtitle: z.string(),
                        tabs: z
                            .object({
                                'assign-users': z.string(),
                                'job-profile-description': z.string(),
                            })
                            .partial(),
                        title: z.string(),
                    })
                    .partial(),
                jobs: z
                    .object({
                        columns: z
                            .object({
                                'application-url': z.string(),
                                creator: z.string(),
                                description: z.string(),
                                duration: z
                                    .object({
                                        title: z.string(),
                                    })
                                    .partial(),
                                'email-placeholder': z.string(),
                                'hiring-manager': z.string(),
                                'job-title': z.string(),
                                language: z.string(),
                                'last-updated': z.string(),
                                location: z.string(),
                                owner: z.string(),
                                'publish-date': z.string(),
                                seniority: z
                                    .object({
                                        title: z.string(),
                                    })
                                    .partial(),
                                shortlist: z
                                    .object({
                                        candidates: z.string(),
                                        candidates_one: z.string(),
                                        candidates_zero: z.string(),
                                        'confirm-remove-dialog': z
                                            .object({
                                                'cancel-button': z.string(),
                                                'confirmation-message': z.string(),
                                                headline: z.string(),
                                                'remove-button': z.string(),
                                            })
                                            .partial(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'skill-groups': z.string(),
                                skills: z.string(),
                                'start-date': z.string(),
                                state: z
                                    .object({
                                        title: z.string(),
                                    })
                                    .partial(),
                                title: z.string(),
                                'work-place': z
                                    .object({
                                        title: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'create-button': z.string(),
                        filters: z
                            .object({
                                'drawer-title': z.string(),
                                'workspace-preferences': z.object({
                                    'sub-title': z.string(),
                                    title: z.string(),
                                }),
                            })
                            .partial(),
                        subtitle: z.string(),
                        tab: z
                            .object({
                                all: z.string(),
                                archived: z.string(),
                                draft: z.string(),
                                hired: z.string(),
                                published: z.string(),
                            })
                            .partial(),
                        title: z.string(),
                        toolbar: z
                            .object({
                                'owner-filter-label': z.string(),
                                'quick-search-placeholder': z.string(),
                                'shortlist-filter-label': z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                'landing-page': z
                    .object({
                        career: z.string(),
                        engagement: z.string(),
                        learning: z.string(),
                        skills: z
                            .object({
                                backend: z.string(),
                                'emotional-design': z.string(),
                                fullstack: z.string(),
                                leadership: z.string(),
                                'problem-solving': z.string(),
                                sensor: z.string(),
                                'solution-selling': z.string(),
                                strategy: z.string(),
                            })
                            .partial(),
                        subtitle: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'learning-opportunities': z
                    .object({
                        columns: z
                            .object({
                                description: z.string(),
                            })
                            .partial(),
                        'course-title': z.string(),
                        'create-button': z.string(),
                        description: z.string(),
                        filters: z
                            .object({
                                'drawer-title': z.string(),
                                'skills-preferences': z
                                    .object({
                                        'selected-skills': z.string(),
                                        'sub-title': z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'workspace-preferences': z
                                    .object({
                                        'sub-title': z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'general-info-title': z.string(),
                        headline: z.string(),
                        headlines: z
                            .object({
                                created: z.string(),
                                'learning-language': z.string(),
                                owner: z.string(),
                                provider: z.string(),
                                skills: z.string(),
                                state: z.string(),
                                title: z.string(),
                                'updated-at': z.string(),
                            })
                            .partial(),
                        language: z.string(),
                        'learning-language': z.string(),
                        navigation: z
                            .object({
                                courses: z.string(),
                            })
                            .partial(),
                        'owner-name': z.string(),
                        provider: z.string(),
                        'skills-title': z.string(),
                        state: z.string(),
                        states: z
                            .object({
                                all: z.string(),
                                archived: z.string(),
                                draft: z.string(),
                                published: z.string(),
                            })
                            .partial(),
                        subtitle: z.string(),
                        title: z.string(),
                        toolbar: z
                            .object({
                                'quick-search-placeholder': z.string(),
                            })
                            .partial(),
                        'trained-skills': z.string(),
                        type: z.string(),
                        types: z
                            .object({
                                course: z.string(),
                            })
                            .partial(),
                        url: z.string(),
                    })
                    .partial(),
                'learning-opportunity-details': z
                    .object({
                        columns: z
                            .object({
                                'featured-to': z.string(),
                            })
                            .partial(),
                        'no-featured-users': z.string(),
                        tabs: z
                            .object({
                                'course-description': z.string(),
                                featured: z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                'learning-opportunity-recommendations-by-skill-ids': z
                    .object({
                        headline: z.string(),
                        'sub-headline': z.string(),
                    })
                    .partial(),
                'my-growth': z
                    .object({
                        bookmarks: z
                            .object({
                                'search-placeholder': z.string(),
                            })
                            .partial(),
                        headline: z.string(),
                        'sub-headline': z.string(),
                        tabs: z
                            .object({
                                bookmarks: z.string(),
                                featured: z.string(),
                            })
                            .partial(),
                        title: z.string(),
                    })
                    .partial(),
                onboarding: z
                    .object({
                        'change-settings-in-profile-settings': z.string(),
                        consent: z
                            .object({
                                'consent-continue': z.string(),
                                headline: z.string(),
                                'open-dialog': z.string(),
                            })
                            .partial(),
                        'create-profile': z
                            .object({
                                'about-you': z.string(),
                                accept: z.string(),
                                'ambitions-missing': z.string(),
                                'create-profile': z.string(),
                                done: z.string(),
                                edit: z.string(),
                                'edit-ambitions-label': z.string(),
                                'edit-description-label': z.string(),
                                'edit-skills-label': z.string(),
                                'edit-your-ambitions': z.string(),
                                'edit-your-skills': z.string(),
                                'found-ambitions': z.string(),
                                'found-language': z.string(),
                                'found-skills': z.string(),
                                'generate-ambitions-failed': z.string(),
                                'generate-ambitions-label': z.string(),
                                'generate-description-failed': z.string(),
                                'generate-description-label': z.string(),
                                'generate-skills-failed': z.string(),
                                'generate-skills-label': z.string(),
                                'hang-on': z.string(),
                                'search-ambitions': z.string(),
                                'search-language': z.string(),
                                'search-skills': z.string(),
                                'skills-missing': z.string(),
                                title: z.string(),
                                'title-long': z.string(),
                                'try-again': z.string(),
                                'we-generate': z.string(),
                                'what-are-you-interested-in': z.string(),
                                'your-ambitions': z.string(),
                                'your-chosen-ambitions': z.string(),
                                'your-chosen-language': z.string(),
                                'your-chosen-skills': z.string(),
                                'your-skills': z.string(),
                            })
                            .partial(),
                        'cv-manual': z
                            .object({
                                description: z.string(),
                            })
                            .partial(),
                        'cv-upload': z
                            .object({
                                continue: z.string(),
                                description: z.string(),
                                dropzone: z
                                    .object({
                                        description: z.string(),
                                        disclaimer: z.string(),
                                        'file-max-size-message': z.string(),
                                        'format-size-error': z.string(),
                                    })
                                    .partial(),
                                errors: z
                                    .object({
                                        analysis: z.string(),
                                        format: z.string(),
                                        parse: z.string(),
                                        'profile-creation': z.string(),
                                        upload: z.string(),
                                    })
                                    .partial(),
                                'processing-skills': z.string(),
                            })
                            .partial(),
                        dialog: z
                            .object({
                                career: z
                                    .object({
                                        'experience-level': z.string(),
                                        'next-step': z.string(),
                                        subtitle: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                finish: z
                                    .object({
                                        action: z.string(),
                                        text: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                settings: z
                                    .object({
                                        notification: z.string(),
                                        subtitle: z.string(),
                                        title: z.string(),
                                        visibility: z.string(),
                                    })
                                    .partial(),
                                skip: z.string(),
                                welcome: z
                                    .object({
                                        'next-step': z.string(),
                                        text: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        dropzone: z
                            .object({
                                'file-max-size-message': z.string(),
                                'format-size-error': z.string(),
                                'linkedin-cta': z.string(),
                                'pdf-cta': z.string(),
                            })
                            .partial(),
                        errors: z
                            .object({
                                analysis: z.string(),
                                format: z.string(),
                                parse: z.string(),
                                'profile-creation': z.string(),
                                upload: z.string(),
                            })
                            .partial(),
                        headline: z.string(),
                        manual: z
                            .object({
                                'back-to-upload': z.string(),
                                submit: z.string(),
                                subtitle: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'onboarding-success': z
                            .object({
                                congratulations: z.string(),
                                'go-to-dashboard': z.string(),
                                'success-message': z.string(),
                            })
                            .partial(),
                        'personal-information': z
                            .object({
                                department: z.string(),
                                'department-helper-text': z.string(),
                                headline: z.string(),
                                seniority: z.string(),
                                'seniority-helper-text': z.string(),
                                'title-helper-text': z.string(),
                            })
                            .partial(),
                        'profile-visibility': z
                            .object({
                                everything: onboardingProfileVisibilitySettingSchema,
                                nothing: onboardingProfileVisibilitySettingSchema,
                                recruiters: onboardingProfileVisibilitySettingSchema,
                                skills: onboardingProfileVisibilitySettingSchema,
                            })
                            .partial(),
                        'specify-role': z
                            .object({
                                description: z.string(),
                                label: z.string(),
                                'skills-selected': z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'sub-headline': z.string(),
                        upload: z
                            .object({
                                linkedin: z.string(),
                                'need-to-select-file': z.string(),
                                'pdf-file': z.string(),
                                'processing-skills': z.string(),
                                'skip-to-manual': z.string(),
                                'step-1': z.string(),
                                'step-1-description': z.string(),
                                'step-1-image-alt': z.string(),
                                'step-2': z.string(),
                                submit: z.string(),
                                subtitle: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        welcome: z
                            .object({
                                'get-started': z.string(),
                                intro: z.string(),
                                subtitle: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                'position-management': z
                    .object({
                        'add-alignment-record': z.string(),
                        'add-assignee': z.string(),
                        'alignment-activity': z.string(),
                        'alignment-pending': z.string(),
                        'alignment-records': z.string(),
                        'assigned-filter': z.string(),
                        cancel: z.string(),
                        'confirm-delete-position': z.string(),
                        'created-by-label': z.string(),
                        delete: z.string(),
                        'delete-position': z.string(),
                        'draft-label': z.string(),
                        employees: z.string(),
                        'focus-skills': z.string(),
                        'invited-filter': z.string(),
                        'job-profile-description-label': z.string(),
                        'last-modified-label': z.string(),
                        'manage-positions': z.string(),
                        'no-alignment-activity': z.string(),
                        'no-alignment-record': z.string(),
                        'no-assignee-found': z.string(),
                        'no-focus-skills': z.string(),
                        'no-job-role-or-job-level-found': z.string(),
                        'no-position-assigned': z.string(),
                        'no-reviewer-found': z.string(),
                        'not-verified': z.string(),
                        'open-chat': z.string(),
                        position: z.string(),
                        'position-assignee': z.string(),
                        'position-assignee-unassigned': z.string(),
                        'position-onboarding-description': z.string(),
                        'position-onboarding-title': z.string(),
                        'required-skills-label': z.string(),
                        'review-by-label': z.string(),
                        'review-by-me-filter': z.string(),
                        'review-jobs': z.string(),
                        'role-level-onboarding-description': z.string(),
                        'role-level-onboarding-title': z.string(),
                        'search-assignees-placeholder': z.string(),
                        'search-employees-placeholder': z.string(),
                        'search-job-roles-or-job-levels-placeholder': z.string(),
                        'search-positions-placeholder': z.string(),
                        'search-reviewers-placeholder': z.string(),
                        'status-label': z.string(),
                        suggested: z.string(),
                        'unassigned-filter': z.string(),
                        verified: z.string(),
                    })
                    .partial(),
                'profile-preview': z
                    .object({
                        'open-profile': z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'recruiter-profile': z
                    .object({
                        'about-me': z
                            .object({
                                placeholder: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        ambitions: z
                            .object({
                                placeholder: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        background: z
                            .object({
                                certificates: z
                                    .object({
                                        placeholder: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'course-history': z
                                    .object({
                                        placeholder: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'education-history': z
                                    .object({
                                        placeholder: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                present: z.string(),
                                'project-history': z
                                    .object({
                                        placeholder: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'publication-history': z
                                    .object({
                                        placeholder: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                title: z.string(),
                                'work-history': z
                                    .object({
                                        placeholder: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        contact: z
                            .object({
                                'e-mail': z.string(),
                                phone: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        details: z
                            .object({
                                'job-title': z.string(),
                                location: z.string(),
                                'seniority-level': z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'general-information': z
                            .object({
                                contact: z.string(),
                                teams: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        languages: z
                            .object({
                                placeholder: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'personal-information': z
                            .object({
                                'job-seeking': z
                                    .object({
                                        'not-open': z.string(),
                                        open: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'location-status': z.string(),
                                'seniority-level': z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        skills: z
                            .object({
                                placeholder: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                settings: z
                    .object({
                        'account-deletion': z
                            .object({
                                button: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'account-details': z
                            .object({
                                email: z.string(),
                                'full-name': z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'data-privacy': z
                            .object({
                                dialog: z
                                    .object({
                                        'load-error': z.string(),
                                        loading: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'download-gdpr-report': z.string(),
                                'open-privacy-statement': z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'email-notification': z
                            .object({
                                message: z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                title: z.string(),
                            })
                            .partial(),
                        language: z
                            .object({
                                title: z.string(),
                            })
                            .partial(),
                        'reset-password': z
                            .object({
                                button: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'skills-banners': z
                            .object({
                                'button-text': z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        title: z.string(),
                        visibility: z
                            .object({
                                title: z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                signup: z
                    .object({
                        'accept-invite-description': z.string(),
                        'accept-invite-title': z.string(),
                        'acknowledge-and-understood': z.string(),
                        'already-have-an-account': z.string(),
                        'by-clicking-continue': z.string(),
                        'company-name': z.string(),
                        'confirm-password': z.string(),
                        email: z.string(),
                        'email-input-description': z.string(),
                        'email-input-title': z.string(),
                        'email-password-to-sign-in': z.string(),
                        'email-sent-description': z.string(),
                        'email-sent-title': z.string(),
                        'error-account-already-exists': z.string(),
                        'error-code-expired': z.string(),
                        'error-email-not-verified': z.string(),
                        'error-invalid-credentials': z.string(),
                        'error-invalid-email-or-code': z.string(),
                        'error-invalid-invitation': z.string(),
                        'error-invitation-already-accepted': z.string(),
                        'error-unknown-reason': z.string(),
                        'first-name': z.string(),
                        'forgot-password': z.string(),
                        'forgot-password-email-sent-description': z.string(),
                        'forgot-password-screen-description': z.string(),
                        'forgot-password-screen-title': z.string(),
                        'getting-things-ready-description': z.string(),
                        'getting-things-ready-title': z.string(),
                        'last-name': z.string(),
                        'name-and-password-step-description': z.string(),
                        password: z.string(),
                        'password-hint': z.string(),
                        'privacy-policy': z.string(),
                        'reset-password-description': z.string(),
                        'reset-password-title': z.string(),
                        'terms-of-use': z.string(),
                        'url-input-hint': z.string(),
                        'verify-email': z.string(),
                        'welcome-back': z.string(),
                        'welcome-to-cobrainer': z.string(),
                    })
                    .partial(),
                'skill-details': z
                    .object({
                        buttons: z
                            .object({
                                'add-to-required-skills': z.string(),
                                'add-to-skill-profile': z.string(),
                                collapse: z.string(),
                                expand: z.string(),
                                'remove-from-skill-profile': z.string(),
                            })
                            .partial(),
                        headlines: z
                            .object({
                                cluster: z.string(),
                                'job-postings': z.string(),
                                'job-title': z.string(),
                                'published-at': z.string(),
                                'related-skills': z.string(),
                                'required-skills': z.string(),
                                'rows-per-page': z.string(),
                                skill: z.string(),
                                'skill-cluster': z.string(),
                                'skill-cluster-information': z.string(),
                                'skill-description': z.string(),
                                'skill-family': z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        messages: z
                            .object({
                                'general-error': z.string(),
                                'no-description': z.string(),
                                'no-job-postings': z.string(),
                                'no-related-skills': z.string(),
                                'no-url-id': z.string(),
                                'please-wait': z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                'talent-directory': z
                    .object({
                        filters: z
                            .object({
                                'skill-gap': z
                                    .object({
                                        subTitle: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'skill-search': z
                                    .object({
                                        placeholder: z.string(),
                                    })
                                    .partial(),
                                skills: z
                                    .object({
                                        subTitle: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'sort-options': z
                            .object({
                                skills: z
                                    .object({
                                        AtoZ: z.string(),
                                        byRelevance: z.string(),
                                        highestToLowest: z.string(),
                                        lowestToHighest: z.string(),
                                        ZtoA: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        title: z.string(),
                    })
                    .partial(),
                'user-certificates': z
                    .object({
                        'acquired-date': z.string(),
                        'acquired-date-in-the-future': z.string(),
                        caption: z.string(),
                        'certificate-title': z.string(),
                        description: z.string(),
                        'expiry-before-acquired': z.string(),
                        'expiry-date': z.string(),
                        'general-info-title': z.string(),
                        issuer: z.string(),
                        'only-one-skill-can-be-added': z.string(),
                        'single-skill-must-be-added': z.string(),
                        skills: z.string(),
                        'skills-list-headline': z.string(),
                        'skills-recommendation-fields-are-missed': z.string(),
                        title: z.string(),
                        'title-add': z.string(),
                        'title-edit': z.string(),
                    })
                    .partial(),
                'user-course-history': z
                    .object({
                        caption: z.string(),
                        'course-title': z.string(),
                        description: z.string(),
                        'end-date': z.string(),
                        from: z.string(),
                        'general-info-title': z.string(),
                        ongoing: z.string(),
                        school: z.string(),
                        skills: z.string(),
                        'skills-list-headline': z.string(),
                        'skills-recommendation-fields-are-missed': z.string(),
                        'skills-recommendation-tooltip': z.string(),
                        'start-date': z.string(),
                        'title-add': z.string(),
                        'title-edit': z.string(),
                        to: z.string(),
                    })
                    .partial(),
                'user-education-history': z
                    .object({
                        caption: z.string(),
                        'currently-studying': z.string(),
                        degree: z.string(),
                        description: z.string(),
                        'end-date': z.string(),
                        'field-of-study': z.string(),
                        from: z.string(),
                        'general-info-title': z.string(),
                        'school-name': z.string(),
                        skills: z.string(),
                        'skills-list-headline': z.string(),
                        'skills-recommendation-fields-are-missed': z.string(),
                        'skills-recommendation-tooltip': z.string(),
                        'start-date': z.string(),
                        'title-add': z.string(),
                        'title-edit': z.string(),
                        to: z.string(),
                    })
                    .partial(),
                'user-profile': z
                    .object({
                        'ambitions-edit': z
                            .object({
                                'add-ambitions': z
                                    .object({
                                        label: z.string(),
                                        'label-disabled': z.string(),
                                        'limit-exceeded': z.string(),
                                        'no-search-results': z.string(),
                                    })
                                    .partial(),
                                headline: z.string(),
                                'number-of-ambitions': z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        banners: z
                            .object({
                                'max-unknown-proficiency-limit': z
                                    .object({
                                        message: z.string(),
                                    })
                                    .partial(),
                                'minimum-skills-requirement': z
                                    .object({
                                        message: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        endorsements: z
                            .object({
                                certificate: z.string(),
                                certificate_one: z.string(),
                                course: z.string(),
                                course_one: z.string(),
                                education: z.string(),
                                education_one: z.string(),
                                project: z.string(),
                                project_one: z.string(),
                                publication: z.string(),
                                publication_one: z.string(),
                                work: z.string(),
                                work_one: z.string(),
                            })
                            .partial(),
                        'language-edit': z
                            .object({
                                'add-languages': z
                                    .object({
                                        label: z.string(),
                                        'label-disabled': z.string(),
                                        'limit-exceeded': z.string(),
                                        'no-search-results': z.string(),
                                    })
                                    .partial(),
                                headline: z.string(),
                                'list-headline': z.string(),
                                'number-of-languages': z.string(),
                            })
                            .partial(),
                        'profile-visibility': z
                            .object({
                                'configure-visibility': z.string(),
                                drawer: z
                                    .object({
                                        'apply-settings-to': z
                                            .object({
                                                'ambitions-label': z.string(),
                                                'apply-button': z.string(),
                                                'apply-to-all-label': z.string(),
                                                'overall-visibility-label': z.string(),
                                                'personal-information-label': z.string(),
                                                'profile-history-label': z.string(),
                                                'reset-button': z.string(),
                                                'skills-label': z.string(),
                                                'visible-to-everyone': z.string(),
                                                'visible-to-me': z.string(),
                                                'visible-to-roles-and-users': z.string(),
                                            })
                                            .partial(),
                                        'information-box': z
                                            .object({
                                                'please-note': z.string(),
                                                text: z.string(),
                                            })
                                            .partial(),
                                        profile: z
                                            .object({
                                                headline: z.string(),
                                                'profile-visibility-label': z.string(),
                                                subtitle: z.string(),
                                                'visibility-label': z.string(),
                                                'visibility-placeholder': z.string(),
                                            })
                                            .partial(),
                                        'reset-button': z.string(),
                                        'reset-configuration': z
                                            .object({
                                                all: z
                                                    .object({
                                                        content: z.string(),
                                                        title: z.string(),
                                                    })
                                                    .partial(),
                                                section: z
                                                    .object({
                                                        content: z.string(),
                                                        title: z.string(),
                                                    })
                                                    .partial(),
                                            })
                                            .partial(),
                                        sections: z
                                            .object({
                                                headline: z.string(),
                                                'search-placeholder': z.string(),
                                                subtitle: z.string(),
                                                'visible-people-label': z.string(),
                                                'visible-roles-label': z.string(),
                                            })
                                            .partial(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'profile-visibility-configuration': z.string(),
                                tooltips: z
                                    .object({
                                        private: z.string(),
                                        public: z.string(),
                                        restricted: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'skill-edit': z
                            .object({
                                'add-skills': z
                                    .object({
                                        label: z.string(),
                                        'label-disabled': z.string(),
                                        'limit-exceeded': z.string(),
                                        'suggested-skills-label': z.string(),
                                    })
                                    .partial(),
                                'container-title': z.string(),
                                headline: z.string(),
                                'number-of-ambitions': z.string(),
                                'number-of-skills': z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                'user-project-history': z
                    .object({
                        caption: z.string(),
                        'company-name': z.string(),
                        description: z.string(),
                        'end-date': z.string(),
                        from: z.string(),
                        'general-info-title': z.string(),
                        location: z.string(),
                        'project-title': z.string(),
                        skills: z.string(),
                        'skills-list-headline': z.string(),
                        'skills-recommendation-fields-are-missed': z.string(),
                        'skills-recommendation-tooltip': z.string(),
                        'start-date': z.string(),
                        'still-ongoing': z.string(),
                        'title-add': z.string(),
                        'title-edit': z.string(),
                        to: z.string(),
                    })
                    .partial(),
                'user-publication-history': z
                    .object({
                        caption: z.string(),
                        date: z.string(),
                        description: z.string(),
                        'general-info-title': z.string(),
                        'publication-name': z.string(),
                        publisher: z.string(),
                        skills: z.string(),
                        'skills-list-headline': z.string(),
                        'skills-recommendation-fields-are-missed': z.string(),
                        'skills-recommendation-tooltip': z.string(),
                        'title-add': z.string(),
                        'title-edit': z.string(),
                    })
                    .partial(),
                'user-work-history': z
                    .object({
                        caption: z.string(),
                        'company-name': z.string(),
                        'currently-working': z.string(),
                        department: z.string(),
                        description: z.string(),
                        'end-date': z.string(),
                        from: z.string(),
                        'general-info-title': z.string(),
                        'job-title': z.string(),
                        location: z.string(),
                        skills: z.string(),
                        'skills-list-headline': z.string(),
                        'skills-recommendation-fields-are-missed': z.string(),
                        'skills-recommendation-tooltip': z.string(),
                        'start-date': z.string(),
                        'title-add': z.string(),
                        'title-edit': z.string(),
                        to: z.string(),
                    })
                    .partial(),
                workspace: z
                    .object({
                        'sub-title': z.string(),
                        title: z.string(),
                    })
                    .partial(),
            })
            .partial(),
        providers: z.partialRecord(
            z.union([z.literal('internal'), z.literal('linkedin'), z.literal('udemy'), z.literal('degreed')]),
            z.string(),
        ),
        roles: z.record(userRoleSchema, roleSchema),
        'side-navigation': z
            .object({
                'aria-label': z.string(),
                'opener-alt-text': z.string(),
            })
            .partial(),
        states: z
            .object({
                active: z.string(),
                all: z.string(),
                archived: z.string(),
                draft: z.string(),
                published: z.string(),
            })
            .partial(),
        success: z
            .object({
                ambitions: z
                    .object({
                        added: z.string(),
                        updated: z.string(),
                    })
                    .partial(),
                'feature-opportunities': z
                    .object({
                        feature: z.string(),
                    })
                    .partial(),
                job: z
                    .object({
                        archived: z.string(),
                        created: z.string(),
                        deleted: z.string(),
                        draft: z.string(),
                        exported: z.string(),
                        hired: z.string(),
                        published: z.string(),
                        updated: z.string(),
                    })
                    .partial(),
                'job-profile': z
                    .object({
                        created: z.string(),
                        deleted: z.string(),
                        edited: z.string(),
                    })
                    .partial(),
                'learning-opportunities': z
                    .object({
                        'added-to-profile': z.string(),
                        created: z.string(),
                        deleted: z.string(),
                        edited: z.string(),
                        exported: z.string(),
                    })
                    .partial(),
                profile: z
                    .object({
                        exported: z.string(),
                        uploaded: z.string(),
                    })
                    .partial(),
                'profile-visibility': z
                    .object({
                        'more-information': z
                            .object({
                                message: z.string(),
                            })
                            .partial(),
                        updated: z.string(),
                    })
                    .partial(),
                settings: z
                    .object({
                        'banner-reset': z.string(),
                        'gdpr-downloaded': z.string(),
                    })
                    .partial(),
                shortlist: z
                    .object({
                        added: z.string(),
                        exported: z.string(),
                        removed: z.string(),
                        reordered: z.string(),
                    })
                    .partial(),
                skills: z
                    .object({
                        'added-to-profile': z.string(),
                        exported: z.string(),
                        'removed-from-profile': z.string(),
                    })
                    .partial(),
                users: z
                    .object({
                        exported: z.string(),
                    })
                    .partial(),
            })
            .partial(),
        trial: z
            .object({
                'contact-sales': z.string(),
                'invite-team': z.string(),
                'keep-using-cobrainer': z.string(),
                'sales-email': z
                    .object({
                        body: z.string(),
                        subject: z.string(),
                    })
                    .partial(),
                sample: z.string(),
                'talk-to-hanns': z.string(),
                'thank-you': z.string(),
                'trial-ended': z.string(),
                'trial-ends-in': z.string(),
            })
            .partial(),
        usermenu: z
            .object({
                'account-settings': z.string(),
                anonymize: z.string(),
                'my-profile': z.string(),
                tooltip: z.string(),
            })
            .partial(),
        validation: z
            .object({
                'date-period-consistency': z.string(),
                'empty-search-results': z.string(),
                'from-list': z.string(),
                'https-url-only': z.string(),
                maxLength: z.string(),
                minLength: z.string(),
                minTranslationLength: z.string(),
                'not-more-than': z.string(),
                oneOf: z.string(),
                required: z.string(),
                'search-term': z.string(),
                withOneOfPrefix: z.string(),
            })
            .partial(),
    })
    .partial();

const recordSchema = z.record(z.string(), z.string());

const recordSchemaRecursive = z.lazy(() => z.record(z.string(), z.union([z.string(), recordSchema])));

const translationAttributeOverridesSchema = z.object({
    'job-profile-tags': recordSchemaRecursive,
    jobProfile: recordSchemaRecursive,
    jobs: recordSchemaRecursive,
    learningOpportunity: recordSchemaRecursive,
    user: recordSchemaRecursive,
});

export type TranslationWithOverrideAttributes = z.infer<typeof translationAttributeOverridesSchema>;

const translationWithOverrideSchema = translationResourceSchema
    .extend({
        attributes: translationAttributeOverridesSchema.partial(),
        providers: z.record(z.string(), z.string()),
        roles: z.record(z.string(), roleSchema),
    })
    .partial();

export const translationResourceOverrideSchema = translationWithOverrideSchema;

export type TranslationResource = RequiredDeep<z.infer<typeof translationResourceSchema>>;

export type TranslationWithOverride = z.infer<typeof translationWithOverrideSchema>;

type StringKeyOf<T> = keyof {
    readonly [K in keyof T as T[K] extends string
        ? `${K & (number | string)}`
        : T[K] extends Record<string, unknown>
          ? `${K & (number | string)}.${StringKeyOf<NonNullable<T[K]>>}`
          : never]: T[K] extends string ? K : never;
} extends `${infer U}`
    ? U extends symbol
        ? never
        : U
    : never;

export type TranslationKey =
    | StringKeyOf<TranslationResource>
    | `attributes.job-profile-tags.${string}`
    | `attributes.jobProfile.${string}`
    | `attributes.jobs.${string}`
    | `attributes.learningOpportunity.${string}`
    | `attributes.user.${string}`
    | `common.login${string}`
    | `providers.${string}`;

export type Translate = (key: TranslationKey, options?: TOptions<any>) => string;
